import {io} from "socket.io-client";
import store from "store/index";
import {addNotification, setConnectionStatus} from "store/reducers/notificationSlice";

class NotificationSocketService {
    constructor() {
        this.socket = null;
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.reconnectDelay = 1000; // Start with 1 second
    }

    /**
     * Connect to notification microservice
     */
    connect(token, storeId) {
        // Prevent multiple simultaneous connection attempts
        if (this.socket?.connected || this.isConnecting) {
            console.log("📡 [NotificationSocket] Already connected or connecting");
            return;
        }

        if (!token || !storeId) {
            console.error("📡 [NotificationSocket] Missing token or storeId");
            return;
        }

        console.log("📡 [NotificationSocket] Connecting to notification service...", {
            storeId,
            url: process.env.REACT_APP_BACKEND_ORIGIN || "http://localhost:4006",
        });

        this.isConnecting = true;

        try {
            this.socket = io(process.env.REACT_APP_BACKEND_ORIGIN || "http://localhost:4006", {
                path: "/foodie",
                auth: {
                    token,
                    store: storeId,
                    type: "restaurant",
                },
                transports: ["websocket", "polling"],
                reconnection: true,
                reconnectionDelay: 1000,
                reconnectionDelayMax: 5000,
                reconnectionAttempts: this.maxReconnectAttempts,
            });

            this.setupEventListeners();
        } catch (error) {
            console.error("📡 [NotificationSocket] Connection error:", error);
            this.isConnecting = false;
            store.dispatch(setConnectionStatus(false));
        }
    }

    /**
     * Setup socket event listeners
     */
    setupEventListeners() {
        if (!this.socket) return;

        // Connection successful
        this.socket.on("connect", () => {
            console.log("✅ [NotificationSocket] Connected successfully");
            this.isConnecting = false;
            this.reconnectAttempts = 0;
            this.reconnectDelay = 1000;
            store.dispatch(setConnectionStatus(true));
        });

        // Connection error
        this.socket.on("connect_error", error => {
            console.error("❌ [NotificationSocket] Connection error:", error.message);
            this.isConnecting = false;
            store.dispatch(setConnectionStatus(false));

            // Implement exponential backoff for reconnection
            if (this.reconnectAttempts < this.maxReconnectAttempts) {
                this.reconnectAttempts++;
                this.reconnectDelay = Math.min(this.reconnectDelay * 2, 10000);
                console.log(
                    `🔄 [NotificationSocket] Reconnecting in ${this.reconnectDelay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`
                );
            }
        });

        // Disconnected
        this.socket.on("disconnect", reason => {
            console.log("⚠️ [NotificationSocket] Disconnected:", reason);
            this.isConnecting = false;
            store.dispatch(setConnectionStatus(false));

            if (reason === "io server disconnect") {
                // Server initiated disconnect, manually reconnect
                console.log("🔄 [NotificationSocket] Server disconnected, attempting to reconnect...");
                setTimeout(() => this.socket?.connect(), 1000);
            }
        });

        // Receive notification event
        this.socket.on("notification", data => {
            console.log("🔔 [NotificationSocket] Received notification:", data);

            // Validate notification data
            if (!data || (!data._id && !data.notificationId)) {
                console.warn(
                    "⚠️ [NotificationSocket] Invalid notification data (missing _id or notificationId):",
                    data
                );
                return;
            }

            const normalizedData = {
                ...data,
                _id: data._id || data.notificationId,
                isRead: data.isRead || false,
                created_at: data.created_at || data.createdAt,
            };
            store.dispatch(addNotification(normalizedData));
            const state = store.getState();
            console.log("🔔 [NotificationSocket] Redux state after dispatch:", {
                notificationsCount: state.notification.notifications.length,
                unreadCount: state.notification.unreadCount,
            });
            this.showToastNotification(normalizedData);

            // Play sound for high priority notifications
            // if (normalizedData.priority === "urgent" || normalizedData.priority === "high") {
            //     this.playNotificationSound();
            // }

            // Dispatch custom event for other components
            console.log("🔔 [NotificationSocket] Dispatching custom event 'new-notification'");
            window.dispatchEvent(new CustomEvent("new-notification", {detail: normalizedData}));
        });

        // ORDER EVENTS - Forwarded from Order Service via Notification Hub
        this.socket.on("order_created", data => {
            console.log("📦 [NotificationSocket] Received order_created event");
            console.log("📦 [NotificationSocket] Full order data:", data);

            window.dispatchEvent(new CustomEvent("order_created", {detail: data}));
        });

        this.socket.on("order_updated", data => {
            console.log("📦 [NotificationSocket] Received order_updated:", data);
            window.dispatchEvent(new CustomEvent("order_updated", {detail: data}));
        });

        this.socket.on("incoming-order", data => {
            console.log("📦 [NotificationSocket] Received incoming-order:", data);
            window.dispatchEvent(new CustomEvent("incoming_order", {detail: data}));
        });

        this.socket.on("preparationTimeSet", data => {
            console.log("⏱️ [NotificationSocket] Received preparationTimeSet:", data);
            window.dispatchEvent(new CustomEvent("preparation_time_set", {detail: data}));
        });

        // DELIVERY EVENTS - Forwarded from Delivery Service via Notification Hub
        this.socket.on("delivery_job_status", data => {
            console.log("🚚 [NotificationSocket] Received delivery_job_status:", data);
            window.dispatchEvent(new CustomEvent("delivery_job_status", {detail: data}));
        });

        this.socket.on("delivery-job-status", data => {
            console.log("🚚 [NotificationSocket] Received delivery-job-status:", data);
            window.dispatchEvent(new CustomEvent("delivery_job_status", {detail: data}));
        });

        // PAYMENT EVENTS - Forwarded from Payment Service via Notification Hub
        this.socket.on("payment_success", data => {
            console.log("💳 [NotificationSocket] Received payment_success:", data);
            window.dispatchEvent(new CustomEvent("payment_success", {detail: data}));
        });

        this.socket.on("payment_failed", data => {
            console.log("💳 [NotificationSocket] Received payment_failed:", data);
            window.dispatchEvent(new CustomEvent("payment_failed", {detail: data}));
        });

        // Listen for all events (debugging)
        if (process.env.NODE_ENV === "development") {
            this.socket.onAny((eventName, ...args) => {
                console.log(`📡 [NotificationSocket] Event: ${eventName}`, args);
            });
        }

        // Reconnection attempt
        this.socket.io.on("reconnect_attempt", attemptNumber => {
            console.log(`🔄 [NotificationSocket] Reconnection attempt ${attemptNumber}`);
        });

        // Reconnected successfully
        this.socket.io.on("reconnect", attemptNumber => {
            console.log(`✅ [NotificationSocket] Reconnected after ${attemptNumber} attempts`);
            this.reconnectAttempts = 0;
            this.reconnectDelay = 1000;
        });
    }

    /**
     * Show toast notification
     * @param {object} notification - Notification data
     */
    showToastNotification(notification) {
        // Dispatch event for Toast component to handle
        window.dispatchEvent(
            new CustomEvent("show-toast-notification", {
                detail: {
                    type: this.getToastType(notification.priority),
                    message: notification.title,
                    description: notification.message,
                    actionUrl: notification.actionUrl,
                },
            })
        );
    }

    /**
     * Get toast type based on priority
     * @param {string} priority - Notification priority
     * @returns {string} Toast type
     */
    getToastType(priority) {
        const typeMap = {
            urgent: "error",
            high: "warning",
            medium: "success",
            low: "info",
        };
        return typeMap[priority] || "info";
    }

    /**
     * Play notification sound
     */
    // playNotificationSound() {
    //     try {
    //         // Create a simple beep sound using Web Audio API
    //         const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    //         const oscillator = audioContext.createOscillator();
    //         const gainNode = audioContext.createGain();

    //         oscillator.connect(gainNode);
    //         gainNode.connect(audioContext.destination);

    //         oscillator.frequency.value = 800;
    //         oscillator.type = "sine";

    //         gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    //         gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

    //         oscillator.start(audioContext.currentTime);
    //         oscillator.stop(audioContext.currentTime + 0.5);
    //     } catch (error) {
    //         console.error("🔊 [NotificationSocket] Error playing sound:", error);
    //     }
    // }

    /**
     * Disconnect from notification service
     */
    disconnect() {
        if (this.socket) {
            console.log("📡 [NotificationSocket] Disconnecting...");
            this.socket.removeAllListeners();
            this.socket.disconnect();
            this.socket = null;
            this.isConnecting = false;
            store.dispatch(setConnectionStatus(false));
        }
    }

    /**
     * Check if socket is connected
     * @returns {boolean}
     */
    isConnected() {
        return this.socket?.connected || false;
    }

    /**
     * Get socket instance
     * @returns {object|null}
     */
    getSocket() {
        return this.socket;
    }
}

// Export singleton instance
const notificationSocketService = new NotificationSocketService();
export default notificationSocketService;
